import React, { Fragment, useState } from "react";
import { BrandMark, Ico } from "./primitives.jsx";
import { MOCK } from "./data.jsx";
import { useMe, apiPost, isDemoMode, navigate } from "./api.jsx";

export function Sidebar({ route, setRoute, section }) {
  const me = useMe();
  const u = me.data;
  const isAuthed = !!(u && u.email);
  const accountType = u?.account_type;
  const isCompany = accountType === "company";
  const isInvestor = accountType === "investor" || u?.is_investor_approved;
  const isAdmin = u?.is_admin;
  const demo = isDemoMode();

  // In demo / unauthenticated mode, show the full tour so the design is
  // navigable. For logged-in users, show only what their role can actually
  // use so nothing in the sidebar 404s or 403s on click.
  const primary = [
    { id: "dashboard", label: "Dashboard", icon: "home" },
    ((isCompany || demo)
      ? { id: "analyzer",  label: "New analysis", icon: "upload" }
      : null),
    ((isInvestor || isAdmin || demo)
      ? { id: "investor-hub", label: "Investor hub", icon: "pipeline" }
      : null),
    ((isInvestor || isAdmin || demo)
      ? { id: "pipeline",  label: "Pipeline", icon: "pipeline" }
      : null),
    ((isCompany || demo)
      ? { id: "my-submissions", label: "My submissions", icon: "folder" }
      : null),
    { id: "results",   label: "Analysis view", icon: "beaker" },
  ].filter(Boolean);

  const admin = [
    { id: "admin",          label: "Overview", icon: "shield" },
    { id: "admin-users",    label: "Users", icon: "people" },
    { id: "admin-analytics",label: "Analytics", icon: "chart" },
    { id: "admin-settings", label: "Settings", icon: "gear" },
  ];

  // "Design" section is a developer aid — only show in demo/unauthenticated mode.
  const meta = [
    { id: "audit",    label: "Audit & direction", icon: "doc" },
    { id: "landing",  label: "Landing page", icon: "doc" },
    { id: "auth",     label: "Login / Register", icon: "doc" },
  ];
  const showMetaSection = demo || !isAuthed;

  return (
    <aside className="sidebar">
      <div className="brand-lockup">
        <BrandMark size={28}/>
        <div className="brand-wordmark">Venzyme<em>.LIFE</em></div>
      </div>
      <div className="nav-section">
        <div className="nav-section-title">Workspace</div>
        {primary.map(n => (
          <div key={n.id} className={"nav-item" + (route === n.id ? " active" : "")} onClick={() => setRoute(n.id)}>
            <Ico n={n.icon}/>
            <span className="nav-label">{n.label}</span>
            {n.badge && <span className="nav-badge">{n.badge}</span>}
          </div>
        ))}
      </div>
      {isAdmin && (
        <div className="nav-section">
          <div className="nav-section-title">Admin</div>
          {admin.map(n => (
            <div key={n.id} className={"nav-item" + (route === n.id ? " active" : "")} onClick={() => setRoute(n.id)}>
              <Ico n={n.icon}/>
              <span className="nav-label">{n.label}</span>
            </div>
          ))}
        </div>
      )}
      {showMetaSection && (
        <div className="nav-section">
          <div className="nav-section-title">Design</div>
          {meta.map(n => (
            <div key={n.id} className={"nav-item" + (route === n.id ? " active" : "")} onClick={() => setRoute(n.id)}>
              <Ico n={n.icon}/>
              <span className="nav-label">{n.label}</span>
            </div>
          ))}
        </div>
      )}
      <UserChip user={u} isAuthed={isAuthed} setRoute={setRoute}/>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Appearance helpers — single source of truth for theme + palette changes.
// Writes the data-* attribute on <body> for the SPA and on <html> for the
// legacy Django reports (so user's choice carries to /dbview/* pages too),
// and persists to localStorage so the pre-paint script in spa.html picks it
// up on the next page load.
// ---------------------------------------------------------------------------
export function setAppearance(key, value) {
  try { localStorage.setItem("vz-" + key, value); } catch {}
  const body = document.body;
  if (body) body.setAttribute("data-" + key, value);
  if (key === "theme") {
    document.documentElement.setAttribute("data-theme", value);
  }
  window.__TWEAKS__ = { ...(window.__TWEAKS__ || {}), [key]: value };
}

export function currentAppearance(key, fallback) {
  try {
    const v = localStorage.getItem("vz-" + key);
    if (v) return v;
  } catch {}
  const body = document.body;
  if (body) {
    const attr = body.getAttribute("data-" + key);
    if (attr) return attr;
  }
  return fallback;
}

function UserChip({ user, isAuthed, setRoute }) {
  const [open, setOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  // Re-render flag when user tweaks appearance so the checkmarks update.
  const [, forceRerender] = useState(0);
  const bump = () => forceRerender(n => n + 1);
  const u = user;

  async function doLogout() {
    try { await apiPost("/auth/logout/"); } catch { /* ignore — still reload */ }
    try { localStorage.removeItem("vz-route"); localStorage.removeItem("vz-analysis-id"); localStorage.removeItem("vz-submission-id"); } catch {}
    location.href = "/";
  }

  const theme = currentAppearance("theme", "light");
  const palette = currentAppearance("palette", "indigo");

  const paletteSwatches = [
    { v: "indigo",      label: "Indigo",      hex: "#3B3B9E" },
    { v: "ultramarine", label: "Ultramarine", hex: "#3D5AA8" },
    { v: "verdigris",   label: "Verdigris",   hex: "#4A8F7C" },
    { v: "cinnabar",    label: "Cinnabar",    hex: "#B44A34" },
    { v: "graphite",    label: "Graphite",    hex: "#2A2F36" },
  ];

  return (
    <div className="sidebar-footer" style={{position:"relative"}}>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position:"absolute",
            bottom:"calc(100% + 4px)", left:16, right:16,
            background:"var(--surface)",
            border:"1px solid var(--line-strong)",
            borderRadius:6,
            padding:6,
            boxShadow:"0 10px 32px color-mix(in srgb, var(--ink) 30%, transparent)",
            zIndex:20,
          }}
        >
          {isAuthed ? (
            <>
              <MenuItem onClick={() => setAppearanceOpen(o => !o)}>
                <span style={{display:"flex", alignItems:"center", gap:6}}>
                  <span>Appearance</span>
                  <span className="dim" style={{fontSize:11}}>— {theme}, {palette}</span>
                </span>
              </MenuItem>
              {appearanceOpen && (
                <div style={{padding:"8px 10px", borderTop:"1px solid var(--line)", borderBottom:"1px solid var(--line)", margin:"4px 0", display:"flex", flexDirection:"column", gap:10}}>
                  <div>
                    <div className="h-eyebrow" style={{marginBottom:6, fontSize:10}}>Theme</div>
                    <div className="seg" style={{display:"flex"}}>
                      {[
                        ["light","Light"],
                        ["dark","Dark"],
                      ].map(([v,l]) => (
                        <button
                          key={v}
                          className={theme === v ? "on" : ""}
                          onClick={() => { setAppearance("theme", v); bump(); }}
                          style={{flex:1, fontSize:11}}
                        >{l}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="h-eyebrow" style={{marginBottom:6, fontSize:10}}>Palette</div>
                    <div style={{display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:6}}>
                      {paletteSwatches.map(s => (
                        <button
                          key={s.v}
                          onClick={() => { setAppearance("palette", s.v); bump(); }}
                          title={s.label}
                          style={{
                            width:"100%", aspectRatio:"1",
                            background:s.hex,
                            border: palette === s.v ? "2px solid var(--accent-hi)" : "1px solid var(--line)",
                            borderRadius:4, cursor:"pointer",
                            padding:0,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <MenuItem onClick={() => { setOpen(false); navigate("account-settings"); }}>Account settings</MenuItem>
              <MenuItem onClick={() => { setOpen(false); navigate("change-password"); }}>Change password</MenuItem>
              <div style={{height:1, background:"var(--line)", margin:"4px 0"}}/>
              <MenuItem danger onClick={doLogout}>Sign out</MenuItem>
            </>
          ) : (
            <>
              <MenuItem onClick={() => setAppearanceOpen(o => !o)}>
                <span style={{display:"flex", alignItems:"center", gap:6}}>
                  <span>Appearance</span>
                  <span className="dim" style={{fontSize:11}}>— {theme}, {palette}</span>
                </span>
              </MenuItem>
              {appearanceOpen && (
                <div style={{padding:"8px 10px", borderTop:"1px solid var(--line)", borderBottom:"1px solid var(--line)", margin:"4px 0", display:"flex", flexDirection:"column", gap:10}}>
                  <div>
                    <div className="h-eyebrow" style={{marginBottom:6, fontSize:10}}>Theme</div>
                    <div className="seg" style={{display:"flex"}}>
                      {[["light","Light"],["dark","Dark"]].map(([v,l]) => (
                        <button
                          key={v}
                          className={theme === v ? "on" : ""}
                          onClick={() => { setAppearance("theme", v); bump(); }}
                          style={{flex:1, fontSize:11}}
                        >{l}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="h-eyebrow" style={{marginBottom:6, fontSize:10}}>Palette</div>
                    <div style={{display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:6}}>
                      {paletteSwatches.map(s => (
                        <button
                          key={s.v}
                          onClick={() => { setAppearance("palette", s.v); bump(); }}
                          title={s.label}
                          style={{
                            width:"100%", aspectRatio:"1",
                            background:s.hex,
                            border: palette === s.v ? "2px solid var(--accent-hi)" : "1px solid var(--line)",
                            borderRadius:4, cursor:"pointer",
                            padding:0,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <MenuItem onClick={() => { setOpen(false); setRoute("auth"); }}>Sign in</MenuItem>
              <MenuItem onClick={() => { setOpen(false); setRoute("auth"); }}>Request access</MenuItem>
            </>
          )}
        </div>
      )}
      <div
        className="user-chip"
        onClick={() => setOpen(o => !o)}
        style={{cursor:"pointer"}}
        title={isAuthed ? "Click for account menu" : "Click to sign in"}
      >
        <div className="user-avatar">{u?.initials || "VZ"}</div>
        <div className="user-meta">
          <div className="user-meta-name">{u?.name || (isAuthed ? "You" : "Guest")}</div>
          <div className="user-meta-role">{u?.role || (isAuthed ? u?.email : "Not signed in")}</div>
        </div>
        <Ico n="arrow" size={12}/>
      </div>
    </div>
  );
}

function MenuItem({ children, onClick, danger }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding:"8px 10px",
        fontSize:13,
        borderRadius:4,
        cursor:"pointer",
        color: danger ? "var(--bad)" : "var(--text)",
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = "var(--surface-2)"}
      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
    >
      {children}
    </div>
  );
}

export function Topbar({ crumbs, actions, onSearch }) {
  return (
    <div className="topbar">
      <div className="crumbs">
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="sep">/</span>}
            <span className={i === crumbs.length - 1 ? "here" : ""}>{c}</span>
          </React.Fragment>
        ))}
      </div>
      <div className="topbar-spacer"/>
      <button className="search" onClick={onSearch} style={{cursor:"pointer"}}>
        <Ico n="search" size={14}/>
        <span>Search companies, analyses, notes…</span>
        <kbd>⌘K</kbd>
      </button>
      {actions}
    </div>
  );
}
